import React, { useState, useEffect, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import PropTypes from 'prop-types'
import './styles.scss'

const ModalPrompt = ({ message, defaultValue = '', onConfirm, onCancel }) => {
  const [inputValue, setInputValue] = useState(defaultValue)
  const inputRef = useRef(null)

  useEffect(() => {
    // Asetetaan fokus input-kenttään, kun komponentti on renderöity
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  useEffect(() => {
    setInputValue(defaultValue)
  }, [defaultValue])

  const handleConfirm = () => {
    if (inputValue.trim() !== '' && typeof onConfirm === 'function') {
      onConfirm(inputValue.trim())
    }
  }

  const handleCancel = () => {
    if (typeof onCancel === 'function') {
      onCancel()
    }
  }

  const handleInputChange = (e) => {
    setInputValue(e.target.value)
  }

  const modalRef = useRef(null)
  const previousFocusRef = useRef(null)

  useEffect(() => {
    // Tallennetaan nykyinen fokus
    previousFocusRef.current = document?.activeElement

    // Asetetaan fokus modaalin sisältöön
    if (modalRef.current) {
      modalRef.current
        .querySelector(
          'button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        .focus()
    }
  }, [])

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      if (typeof onCancel === 'function') {
        onCancel()
      }
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      handleConfirm()
      return
    }
    // Estetään Tab-näppäimen toiminto modaalin ulkopuolelle
    if (e.key === 'Tab') {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      const firstFocusable = focusableElements[0]
      const lastFocusable = focusableElements[focusableElements.length - 1]

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault()
          lastFocusable.focus()
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault()
          firstFocusable.focus()
        }
      }
    }
  }

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      aria-label={message}
      tabIndex="-1"
    >
      <div className="modal-content alert" ref={modalRef} onKeyDown={handleKeyDown}>
        <button className="close-button" onClick={handleCancel} aria-label="Sulje">
          <FontAwesomeIcon
            icon={['far', 'times-circle']}
            className="template-icon"
            size="2x"
          />
        </button>
        <label htmlFor="prompt-input" className="prompt-label">
          {message}
        </label>
        <input
          id="prompt-input"
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          ref={inputRef}
          className="prompt-input"
          spellCheck="false"
        />
        <div className="button-group">
          <button
            className="dialogue-button"
            onClick={handleConfirm}
            disabled={inputValue.trim() === ''}
          >
            Valmis
          </button>
          <button className="dialogue-button gray-button" onClick={handleCancel}>
            Sulje
          </button>
        </div>
      </div>
    </div>
  )
}

export default ModalPrompt

ModalPrompt.propTypes = {
  message: PropTypes.string.isRequired,
  defaultValue: PropTypes.string,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
}

ModalPrompt.defaultProps = {
  defaultValue: '',
  onConfirm: null,
  onCancel: null,
}
